import React, { useEffect, useState, useMemo } from 'react';
import { getVoteAnalysis } from '../api/votes';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  ThumbsUp,
  ThumbsDown,
  BarChart3,
  Loader2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  ArrowUpDown,
} from 'lucide-react';

interface ChunkStat {
  chunk_id: string;
  upvotes: number;
  downvotes: number;
  sentiment: 'positive' | 'negative' | 'mixed';
  queries: string[];
}

interface IntegrationData {
  chunks: Record<string, ChunkStat>;
  total_upvotes: number;
  total_downvotes: number;
}

interface VoteRecord {
  vote_id: string;
  vote: 'up' | 'down';
  query: string;
  session_id: string;
  timestamp: string;
  model: string;
  processing_time: number;
  integrations: string[];
  rag_chunks_by_integration: Record<string, string[]>;
  workgroup: string;
}

interface Summary {
  total_votes: number;
  total_upvotes: number;
  total_downvotes: number;
  votes_with_chunks: number;
  votes_without_chunks: number;
  unique_chunks: number;
  positive_chunks: number;
  negative_chunks: number;
  mixed_chunks: number;
}

type SortField = 'chunk_id' | 'upvotes' | 'downvotes' | 'total' | 'sentiment';
type SortDir = 'asc' | 'desc';

const VoteAnalysis: React.FC = () => {
  const [votes, setVotes] = useState<VoteRecord[]>([]);
  const [integrations, setIntegrations] = useState<Record<string, IntegrationData>>({});
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'chunks' | 'votes'>('chunks');

  // Chunk table state
  const [sortField, setSortField] = useState<SortField>('downvotes');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const [expandedIntegrations, setExpandedIntegrations] = useState<Set<string>>(new Set());

  // Vote log filters
  const [voteFilter, setVoteFilter] = useState<'all' | 'up' | 'down'>('all');
  const [chunkFilter, setChunkFilter] = useState<'all' | 'with' | 'without'>('all');

  useEffect(() => {
    loadData();
  }, []);

  // Auto-expand all integrations on data load
  useEffect(() => {
    setExpandedIntegrations(new Set(Object.keys(integrations)));
  }, [integrations]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getVoteAnalysis();
      setVotes(data.votes || []);
      setIntegrations(data.integrations || {});
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load vote analysis');
    } finally {
      setLoading(false);
    }
  };

  const toggleIntegration = (name: string) => {
    setExpandedIntegrations(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleChunk = (key: string) => {
    setExpandedChunks(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortChunks = (chunks: ChunkStat[]): ChunkStat[] => {
    return [...chunks].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      if (sortField === 'total') {
        aVal = a.upvotes + a.downvotes;
        bVal = b.upvotes + b.downvotes;
      } else if (sortField === 'sentiment') {
        const order = { negative: 0, mixed: 1, positive: 2 };
        aVal = order[a.sentiment];
        bVal = order[b.sentiment];
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredVotes = useMemo(() => {
    let filtered = votes;
    if (voteFilter !== 'all') {
      filtered = filtered.filter(v => v.vote === voteFilter);
    }
    if (chunkFilter === 'with') {
      filtered = filtered.filter(v => {
        const chunks = v.rag_chunks_by_integration || {};
        return Object.values(chunks).some(ids => ids && ids.length > 0);
      });
    } else if (chunkFilter === 'without') {
      filtered = filtered.filter(v => {
        const chunks = v.rag_chunks_by_integration || {};
        return !Object.values(chunks).some(ids => ids && ids.length > 0);
      });
    }
    return filtered.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  }, [votes, voteFilter, chunkFilter]);

  const sentimentBadge = (sentiment: string) => {
    if (sentiment === 'positive') {
      return <Badge data-testid="sentiment-positive" className="bg-green-100 text-green-800 border-green-200">Positive</Badge>;
    }
    if (sentiment === 'negative') {
      return <Badge data-testid="sentiment-negative" className="bg-red-100 text-red-800 border-red-200">Negative</Badge>;
    }
    return <Badge data-testid="sentiment-mixed" className="bg-yellow-100 text-yellow-800 border-yellow-200">Mixed</Badge>;
  };

  const SortHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <th
      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="w-3 h-3" />
        {sortField === field && <span className="text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="vote-analysis-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">Loading vote analysis...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="vote-analysis-error">
        <p className="text-red-500">{error}</p>
        <button onClick={loadData} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="vote-analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-900">Vote Pattern Analysis</h1>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          data-testid="refresh-button"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="summary-cards">
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Votes</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold" data-testid="total-votes">{summary.total_votes}</div>
              <div className="flex gap-3 text-sm mt-1">
                <span className="text-green-600 flex items-center gap-1">
                  <ThumbsUp className="w-3 h-3" /> {summary.total_upvotes}
                </span>
                <span className="text-red-600 flex items-center gap-1">
                  <ThumbsDown className="w-3 h-3" /> {summary.total_downvotes}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Chunk Coverage</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold" data-testid="votes-with-chunks">{summary.votes_with_chunks}</div>
              <div className="text-sm text-gray-500 mt-1">
                {summary.votes_without_chunks} without chunks
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Unique Chunks</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold" data-testid="unique-chunks">{summary.unique_chunks}</div>
              <div className="text-sm text-gray-500 mt-1">
                across {Object.keys(integrations).length} integration{Object.keys(integrations).length !== 1 ? 's' : ''}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Chunk Sentiment</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="flex gap-2 flex-wrap" data-testid="sentiment-summary">
                <span className="text-green-600 text-sm">{summary.positive_chunks} positive</span>
                <span className="text-red-600 text-sm">{summary.negative_chunks} negative</span>
                <span className="text-yellow-600 text-sm">{summary.mixed_chunks} mixed</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b" data-testid="tabs">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'chunks'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('chunks')}
          data-testid="tab-chunks"
        >
          Chunk Analysis
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'votes'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('votes')}
          data-testid="tab-votes"
        >
          Vote Log ({votes.length})
        </button>
      </div>

      {/* Chunk Analysis Tab */}
      {activeTab === 'chunks' && (
        <div data-testid="chunk-analysis">
          {Object.keys(integrations).length === 0 ? (
            <div className="text-center text-gray-500 py-12">No chunk data available. Votes may have been cast without active sessions.</div>
          ) : (
            Object.entries(integrations).map(([integrationName, idata]) => {
              const chunks = Object.values(idata.chunks);
              const sorted = sortChunks(chunks);
              const isExpanded = expandedIntegrations.has(integrationName);

              return (
                <div key={integrationName} className="mb-4 border rounded-lg overflow-hidden" data-testid={`integration-${integrationName}`}>
                  {/* Integration header */}
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    onClick={() => toggleIntegration(integrationName)}
                    data-testid={`integration-toggle-${integrationName}`}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-semibold text-gray-900">{integrationName}</span>
                      <Badge className="bg-gray-200 text-gray-700">{chunks.length} chunk{chunks.length !== 1 ? 's' : ''}</Badge>
                    </div>
                    <div className="flex gap-3 text-sm">
                      <span className="text-green-600 flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" /> {idata.total_upvotes}
                      </span>
                      <span className="text-red-600 flex items-center gap-1">
                        <ThumbsDown className="w-3 h-3" /> {idata.total_downvotes}
                      </span>
                    </div>
                  </button>

                  {/* Chunk table */}
                  {isExpanded && (
                    <table className="w-full" data-testid={`chunk-table-${integrationName}`}>
                      <thead className="bg-gray-50 border-t">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <SortHeader field="chunk_id" label="Chunk ID" />
                          <SortHeader field="upvotes" label="Upvotes" />
                          <SortHeader field="downvotes" label="Downvotes" />
                          <SortHeader field="total" label="Total" />
                          <SortHeader field="sentiment" label="Sentiment" />
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sorted.map(chunk => {
                          const rowKey = `${integrationName}:${chunk.chunk_id}`;
                          const isRowExpanded = expandedChunks.has(rowKey);
                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => toggleChunk(rowKey)}
                                data-testid={`chunk-row-${chunk.chunk_id}`}
                              >
                                <td className="px-3 py-2 text-gray-400">
                                  {isRowExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                </td>
                                <td className="px-3 py-2 text-sm font-mono text-gray-700">{chunk.chunk_id}</td>
                                <td className="px-3 py-2 text-sm text-green-600 font-medium">{chunk.upvotes}</td>
                                <td className="px-3 py-2 text-sm text-red-600 font-medium">{chunk.downvotes}</td>
                                <td className="px-3 py-2 text-sm font-medium">{chunk.upvotes + chunk.downvotes}</td>
                                <td className="px-3 py-2">{sentimentBadge(chunk.sentiment)}</td>
                              </tr>
                              {isRowExpanded && chunk.queries.length > 0 && (
                                <tr>
                                  <td colSpan={6} className="px-6 py-3 bg-gray-50">
                                    <div className="text-xs font-medium text-gray-500 mb-2">Associated Queries:</div>
                                    <ul className="space-y-1">
                                      {chunk.queries.map((q, i) => (
                                        <li key={i} className="text-sm text-gray-700 pl-2 border-l-2 border-gray-300">
                                          {q}
                                        </li>
                                      ))}
                                    </ul>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Vote Log Tab */}
      {activeTab === 'votes' && (
        <div data-testid="vote-log">
          {/* Filters */}
          <div className="flex gap-4 mb-4" data-testid="vote-filters">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Vote:</span>
              {(['all', 'up', 'down'] as const).map(f => (
                <button
                  key={f}
                  className={`px-2 py-1 text-xs rounded ${
                    voteFilter === f ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  onClick={() => setVoteFilter(f)}
                  data-testid={`filter-vote-${f}`}
                >
                  {f === 'all' ? 'All' : f === 'up' ? 'Upvotes' : 'Downvotes'}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Chunks:</span>
              {(['all', 'with', 'without'] as const).map(f => (
                <button
                  key={f}
                  className={`px-2 py-1 text-xs rounded ${
                    chunkFilter === f ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  onClick={() => setChunkFilter(f)}
                  data-testid={`filter-chunks-${f}`}
                >
                  {f === 'all' ? 'All' : f === 'with' ? 'With Chunks' : 'Without Chunks'}
                </button>
              ))}
            </div>
          </div>

          {filteredVotes.length === 0 ? (
            <div className="text-center text-gray-500 py-12">No votes match the current filters.</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full" data-testid="vote-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vote</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Query</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Workgroup</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Chunks</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredVotes.map(v => {
                    const chunkCount = Object.values(v.rag_chunks_by_integration || {}).reduce(
                      (sum, ids) => sum + (ids?.length || 0), 0
                    );
                    return (
                      <tr key={v.vote_id} className="hover:bg-gray-50" data-testid={`vote-row-${v.vote_id}`}>
                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {v.timestamp ? new Date(v.timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="px-3 py-2">
                          {v.vote === 'up' ? (
                            <ThumbsUp className="w-4 h-4 text-green-500" />
                          ) : (
                            <ThumbsDown className="w-4 h-4 text-red-500" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700 max-w-md truncate">
                          {v.query || '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500 font-mono">{v.model || '-'}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">{v.workgroup || '-'}</td>
                        <td className="px-3 py-2 text-sm">
                          {chunkCount > 0 ? (
                            <Badge className="bg-blue-100 text-blue-700">{chunkCount}</Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">none</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoteAnalysis;
